import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import logo from './image/etf_logo.png';

const ETFInvestmentAnalysis = () => {
    // Helper function to get cached value from localStorage
    const getCachedValue = (key, defaultValue) => {
        try {
            const cached = localStorage.getItem(key);
            if (cached !== null) {
                return JSON.parse(cached);
            }
        } catch (error) {
            console.error(`Error reading ${key} from localStorage:`, error);
        }
        return defaultValue;
    };

    // Initialize state with cached values or defaults
    const [initialInvestment, setInitialInvestment] = useState(() => getCachedValue('initialInvestment', 50000));
    const [monthlyContribution, setMonthlyContribution] = useState(() => getCachedValue('monthlyContribution', 1000));
    const [investmentYears, setInvestmentYears] = useState(() => getCachedValue('investmentYears', 10));
    const [portfolioType, setPortfolioType] = useState(() => getCachedValue('portfolioType', 'balanced'));
    const [vasPercentage, setVasPercentage] = useState(() => getCachedValue('vasPercentage', 50));
    const [vasReturn, setVasReturn] = useState(() => getCachedValue('vasReturn', 7.5));
    const [vgsReturn, setVgsReturn] = useState(() => getCachedValue('vgsReturn', 9.0));
    const [vasDividend, setVasDividend] = useState(() => getCachedValue('vasDividend', 3.3));
    const [vgsDividend, setVgsDividend] = useState(() => getCachedValue('vgsDividend', 2.0));
    const [managementFee, setManagementFee] = useState(() => getCachedValue('managementFee', 0.13));
    const [inflationRate, setInflationRate] = useState(() => getCachedValue('inflationRate', 2.5));
    const [reinvestDividends, setReinvestDividends] = useState(() => getCachedValue('reinvestDividends', true));
    const [taxRate, setTaxRate] = useState(() => getCachedValue('taxRate', 32.5));
    const [frankedPercentage, setFrankedPercentage] = useState(() => getCachedValue('frankedPercentage', 75));

    // Debt recycling feature states
    const [debtRecyclingMode, setDebtRecyclingMode] = useState(() => getCachedValue('debtRecyclingMode', false));
    const [loanAmount, setLoanAmount] = useState(() => getCachedValue('loanAmount', 50000));
    const [loanInterestRate, setLoanInterestRate] = useState(() => getCachedValue('loanInterestRate', 6.5));
    const [loanType, setLoanType] = useState(() => getCachedValue('loanType', 'IO')); // 'IO' or 'PI'
    const [loanTerm, setLoanTerm] = useState(() => getCachedValue('loanTerm', 30));
    const [monthlyLoan, setMonthlyLoan] = useState(() => getCachedValue('monthlyLoan', 0));

    // Cache values to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem('initialInvestment', JSON.stringify(initialInvestment));
        } catch (error) {
            console.error('Error saving initialInvestment to localStorage:', error);
        }
    }, [initialInvestment]);

    useEffect(() => {
        try {
            localStorage.setItem('monthlyContribution', JSON.stringify(monthlyContribution));
        } catch (error) {
            console.error('Error saving monthlyContribution to localStorage:', error);
        }
    }, [monthlyContribution]);

    useEffect(() => {
        try {
            localStorage.setItem('investmentYears', JSON.stringify(investmentYears));
        } catch (error) {
            console.error('Error saving investmentYears to localStorage:', error);
        }
    }, [investmentYears]);

    useEffect(() => {
        try {
            localStorage.setItem('portfolioType', JSON.stringify(portfolioType));
        } catch (error) {
            console.error('Error saving portfolioType to localStorage:', error);
        }
    }, [portfolioType]);

    useEffect(() => {
        try {
            localStorage.setItem('vasPercentage', JSON.stringify(vasPercentage));
        } catch (error) {
            console.error('Error saving vasPercentage to localStorage:', error);
        }
    }, [vasPercentage]);

    useEffect(() => {
        try {
            localStorage.setItem('vasReturn', JSON.stringify(vasReturn));
        } catch (error) {
            console.error('Error saving vasReturn to localStorage:', error);
        }
    }, [vasReturn]);

    useEffect(() => {
        try {
            localStorage.setItem('vgsReturn', JSON.stringify(vgsReturn));
        } catch (error) {
            console.error('Error saving vgsReturn to localStorage:', error);
        }
    }, [vgsReturn]);

    useEffect(() => {
        try {
            localStorage.setItem('vasDividend', JSON.stringify(vasDividend));
        } catch (error) {
            console.error('Error saving vasDividend to localStorage:', error);
        }
    }, [vasDividend]);

    useEffect(() => {
        try {
            localStorage.setItem('vgsDividend', JSON.stringify(vgsDividend));
        } catch (error) {
            console.error('Error saving vgsDividend to localStorage:', error);
        }
    }, [vgsDividend]);

    useEffect(() => {
        try {
            localStorage.setItem('managementFee', JSON.stringify(managementFee));
        } catch (error) {
            console.error('Error saving managementFee to localStorage:', error);
        }
    }, [managementFee]);

    useEffect(() => {
        try {
            localStorage.setItem('inflationRate', JSON.stringify(inflationRate));
        } catch (error) {
            console.error('Error saving inflationRate to localStorage:', error);
        }
    }, [inflationRate]);

    useEffect(() => {
        try {
            localStorage.setItem('reinvestDividends', JSON.stringify(reinvestDividends));
        } catch (error) {
            console.error('Error saving reinvestDividends to localStorage:', error);
        }
    }, [reinvestDividends]);

    useEffect(() => {
        try {
            localStorage.setItem('taxRate', JSON.stringify(taxRate));
        } catch (error) {
            console.error('Error saving taxRate to localStorage:', error);
        }
    }, [taxRate]);

    useEffect(() => {
        try {
            localStorage.setItem('frankedPercentage', JSON.stringify(frankedPercentage));
        } catch (error) {
            console.error('Error saving frankedPercentage to localStorage:', error);
        }
    }, [frankedPercentage]);

    // Debt recycling cache
    useEffect(() => {
        try { localStorage.setItem('debtRecyclingMode', JSON.stringify(debtRecyclingMode)); } catch (e) { console.error('Error saving debtRecyclingMode', e); }
    }, [debtRecyclingMode]);

    useEffect(() => {
        try { localStorage.setItem('loanAmount', JSON.stringify(loanAmount)); } catch (e) { console.error('Error saving loanAmount', e); }
    }, [loanAmount]);

    useEffect(() => {
        try { localStorage.setItem('loanInterestRate', JSON.stringify(loanInterestRate)); } catch (e) { console.error('Error saving loanInterestRate', e); }
    }, [loanInterestRate]);

    useEffect(() => {
        try { localStorage.setItem('loanType', JSON.stringify(loanType)); } catch (e) { console.error('Error saving loanType', e); }
    }, [loanType]);

    useEffect(() => {
        try { localStorage.setItem('loanTerm', JSON.stringify(loanTerm)); } catch (e) { console.error('Error saving loanTerm', e); }
    }, [loanTerm]);

    useEffect(() => {
        try { localStorage.setItem('monthlyLoan', JSON.stringify(monthlyLoan)); } catch (e) { console.error('Error saving monthlyLoan', e); }
    }, [monthlyLoan]);

    const vgsPercentage = 100 - vasPercentage;

    // Portfolio presets
    const applyPreset = (preset) => {
        switch(preset) {
            case 'conservative':
                setVasPercentage(60);
                setPortfolioType('conservative');
                break;
            case 'balanced':
                setVasPercentage(50);
                setPortfolioType('balanced');
                break;
            case 'growth':
                setVasPercentage(40);
                setPortfolioType('growth');
                break;
            case 'allVas':
                setVasPercentage(100);
                setPortfolioType('allVas');
                break;
            case 'allVgs':
                setVasPercentage(0);
                setPortfolioType('allVgs');
                break;
            default:
                setPortfolioType('custom');
        }
    };

    const calculations = useMemo(() => {
        const vasAllocation = vasPercentage / 100;
        const vgsAllocation = vgsPercentage / 100;

        const blendedReturn = (vasReturn * vasAllocation) + (vgsReturn * vgsAllocation);
        const blendedDividend = (vasDividend * vasAllocation) + (vgsDividend * vgsAllocation);
        const blendedGrowth = blendedReturn - blendedDividend;

        const projection = [];
        let totalValue = initialInvestment;
        let totalContributed = initialInvestment;
        let totalDividends = 0;
        let totalGrowth = 0;
        let totalFees = 0;
        let totalInterestPaid = 0;
        let totalTaxSaved = 0;
        let remainingLoan = debtRecyclingMode ? loanAmount : 0;

        const monthlyRate = loanInterestRate / 100 / 12;
        const numPayments = loanTerm * 12;
        const monthlyPIPayment = loanType === 'PI' && debtRecyclingMode && loanAmount > 0
            ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)
            : 0;

        for (let year = 1; year <= investmentYears; year++) {
            const startValue = totalValue;
            const yearlyContributions = monthlyContribution * 12;
            totalContributed += yearlyContributions;

            // Add contributions throughout the year (averaged)
            totalValue += yearlyContributions;

            // Calculate dividends
            const yearDividends = totalValue * (blendedDividend / 100);
            totalDividends += yearDividends;

            // Calculate franking credits on VAS dividends
            const vasDividends = (totalValue * vasAllocation) * (vasDividend / 100);
            const frankingCredits = vasDividends * (frankedPercentage / 100) * (30 / 70);

            // Tax on dividends (after franking credits)
            const taxOnDividends = reinvestDividends
                ? (yearDividends + frankingCredits) * (taxRate / 100) - frankingCredits
                : 0;

            // Reinvest dividends if selected
            if (reinvestDividends) {
                totalValue += yearDividends - Math.max(0, taxOnDividends);
            }

            // Apply capital growth
            const yearGrowth = totalValue * (blendedGrowth / 100);
            totalGrowth += yearGrowth;
            totalValue += yearGrowth;

            // Deduct management fees
            const yearFees = totalValue * (managementFee / 100);
            totalFees += yearFees;
            totalValue -= yearFees;

            // Debt recycling calculations per year
            let yearInterest = 0;
            let yearPrincipal = 0;
            let yearTaxSaved = 0;
            let afterTaxInterestCost = 0;

            if (debtRecyclingMode) {
                // Add monthly loan contributions to the loan balance
                const yearlyLoanAddition = monthlyLoan * 12;
                remainingLoan += yearlyLoanAddition;

                if (remainingLoan > 0) {
                    if (loanType === 'IO') {
                        yearInterest = remainingLoan * (loanInterestRate / 100);
                        yearPrincipal = 0;
                    } else {
                        // For P&I loans with monthly additions, we need to recalculate based on current balance
                        // Use simplified approach: calculate interest on average balance, then apply principal reduction
                        const annualPayment = monthlyPIPayment * 12;
                        yearInterest = remainingLoan * (loanInterestRate / 100);
                        yearPrincipal = Math.min(annualPayment - yearInterest, remainingLoan);
                        remainingLoan = Math.max(0, remainingLoan - yearPrincipal);
                    }
                    yearTaxSaved = yearInterest * (taxRate / 100);
                    afterTaxInterestCost = yearInterest - yearTaxSaved;
                    totalInterestPaid += yearInterest;
                    totalTaxSaved += yearTaxSaved;
                }
            }

            const realReturn = totalValue / Math.pow(1 + inflationRate / 100, year);
            const gainLoss = totalValue - totalContributed;
            const returnPercent = ((totalValue - totalContributed) / totalContributed) * 100;
            const netCashFlow = debtRecyclingMode
                ? yearDividends - afterTaxInterestCost - (loanType === 'PI' ? yearPrincipal : 0)
                : yearDividends;

            projection.push({
                year,
                totalValue: Math.round(totalValue),
                contributed: Math.round(totalContributed),
                growth: Math.round(totalGrowth),
                dividends: Math.round(totalDividends),
                fees: Math.round(totalFees),
                realValue: Math.round(realReturn),
                gainLoss: Math.round(gainLoss),
                returnPercent: returnPercent.toFixed(1),
                vasValue: Math.round(totalValue * vasAllocation),
                vgsValue: Math.round(totalValue * vgsAllocation),
                loanBalance: Math.round(remainingLoan),
                yearInterest: Math.round(yearInterest),
                yearPrincipal: Math.round(yearPrincipal),
                yearTaxSaved: Math.round(yearTaxSaved),
                afterTaxInterestCost: Math.round(afterTaxInterestCost),
                netCashFlow: Math.round(netCashFlow),
                equity: Math.round(totalValue - remainingLoan)
            });
        }

        const finalYear = projection[projection.length - 1];
        const annualDividendIncome = finalYear.totalValue * (blendedDividend / 100);
        const monthlyDividendIncome = annualDividendIncome / 12;

        const effectiveInterestRate = debtRecyclingMode ? loanInterestRate * (1 - taxRate / 100) : 0;
        return {
            blendedReturn,
            blendedDividend,
            blendedGrowth,
            projection,
            finalValue: finalYear.totalValue,
            totalContributed: finalYear.contributed,
            totalGain: finalYear.gainLoss,
            totalReturnPercent: finalYear.returnPercent,
            annualDividendIncome,
            monthlyDividendIncome,
            totalDividends: finalYear.dividends,
            totalFees: finalYear.fees,
            totalInterestPaid,
            totalTaxSaved,
            effectiveInterestRate,
            finalLoanBalance: finalYear.loanBalance,
            finalEquity: finalYear.equity
        };
    }, [initialInvestment, monthlyContribution, investmentYears, vasPercentage, vgsPercentage,
        vasReturn, vgsReturn, vasDividend, vgsDividend, managementFee, inflationRate,
        reinvestDividends, taxRate, frankedPercentage, debtRecyclingMode, loanAmount,
        loanInterestRate, loanType, loanTerm, monthlyLoan]);

    const COLORS = ['#3b82f6', '#10b981'];

    const pieData = [
        { name: 'VAS', value: vasPercentage },
        { name: 'VGS', value: vgsPercentage }
    ];

    return (
        <div className="p-6 max-w-6xl mx-auto bg-gray-50">
            <div className="flex items-center justify-center gap-4 mb-6">
                <img src={logo} alt="ETF Horizon Logo" className="h-16 w-16" />
                <h1 className="text-3xl font-bold text-gray-800">ETF Investment Analysis Tool</h1>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Investment Parameters</h2>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Investment Strategy</label>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setDebtRecyclingMode(false)}
                            className={`flex-1 py-2 px-4 rounded font-medium transition ${!debtRecyclingMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                            Cash Investment
                        </button>
                        <button
                            onClick={() => setDebtRecyclingMode(true)}
                            className={`flex-1 py-2 px-4 rounded font-medium transition ${debtRecyclingMode ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                            Debt Recycling
                        </button>
                    </div>
                </div>

                {debtRecyclingMode && (
                    <div className="mb-6 border border-purple-200 rounded-lg p-4 bg-purple-50">
                        <h3 className="text-purple-800 font-semibold mb-3">Debt Recycling Settings</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-purple-800 mb-2">Loan Amount: ${loanAmount.toLocaleString()}</label>
                                <input type="range" min="0" max="500000" step="1000" value={loanAmount} onChange={(e) => setLoanAmount(Number(e.target.value))} className="w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-purple-800 mb-2">Monthly Loan: ${monthlyLoan}</label>
                                <input type="range" min="0" max="5000" step="100" value={monthlyLoan} onChange={(e) => setMonthlyLoan(Number(e.target.value))} className="w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-purple-800 mb-2">Interest Rate: {loanInterestRate.toFixed(2)}%</label>
                                <input type="range" min="0" max="15" step="0.01" value={loanInterestRate} onChange={(e) => setLoanInterestRate(Number(e.target.value))} className="w-full" />
                                <p className="text-xs text-purple-700 mt-1">After-tax effective rate at {taxRate}% tax: {calculations.effectiveInterestRate.toFixed(2)}%</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-purple-800 mb-2">Loan Type</label>
                                <div className="flex gap-2">
                                    <button onClick={() => setLoanType('IO')} className={`flex-1 py-2 px-4 rounded ${loanType === 'IO' ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 border border-purple-300'}`}>Interest Only</button>
                                    <button onClick={() => setLoanType('PI')} className={`flex-1 py-2 px-4 rounded ${loanType === 'PI' ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 border border-purple-300'}`}>P & I</button>
                                </div>
                            </div>
                            {loanType === 'PI' && (
                                <div>
                                    <label className="block text-sm font-medium text-purple-800 mb-2">Loan Term: {loanTerm} years</label>
                                    <input type="range" min="15" max="30" step="1" value={loanTerm} onChange={(e) => setLoanTerm(Number(e.target.value))} className="w-full" />
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Initial Investment: ${initialInvestment.toLocaleString()}
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="200000"
                            step="5000"
                            value={initialInvestment}
                            onChange={(e) => setInitialInvestment(Number(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Monthly Contribution: ${monthlyContribution}
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="5000"
                            step="100"
                            value={monthlyContribution}
                            onChange={(e) => setMonthlyContribution(Number(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Investment Period: {investmentYears} years
                        </label>
                        <input
                            type="range"
                            min="1"
                            max="30"
                            step="1"
                            value={investmentYears}
                            onChange={(e) => setInvestmentYears(Number(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tax Rate: {taxRate}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="45"
                            step="0.5"
                            value={taxRate}
                            onChange={(e) => setTaxRate(Number(e.target.value))}
                            className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Your marginal tax rate</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Inflation Rate: {inflationRate}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="5"
                            step="0.1"
                            value={inflationRate}
                            onChange={(e) => setInflationRate(Number(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reinvest Dividends
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setReinvestDividends(true)}
                                className={`flex-1 py-2 px-4 rounded font-medium transition ${
                                    reinvestDividends
                                        ? 'bg-green-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Yes
                            </button>
                            <button
                                onClick={() => setReinvestDividends(false)}
                                className={`flex-1 py-2 px-4 rounded font-medium transition ${
                                    !reinvestDividends
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                No
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Portfolio Allocation</h2>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Portfolio Presets
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <button
                            onClick={() => applyPreset('conservative')}
                            className={`py-2 px-4 rounded font-medium transition ${
                                portfolioType === 'conservative'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Conservative
                            <span className="block text-xs">60% VAS</span>
                        </button>
                        <button
                            onClick={() => applyPreset('balanced')}
                            className={`py-2 px-4 rounded font-medium transition ${
                                portfolioType === 'balanced'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Balanced
                            <span className="block text-xs">50/50</span>
                        </button>
                        <button
                            onClick={() => applyPreset('growth')}
                            className={`py-2 px-4 rounded font-medium transition ${
                                portfolioType === 'growth'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Growth
                            <span className="block text-xs">40% VAS</span>
                        </button>
                        <button
                            onClick={() => applyPreset('allVas')}
                            className={`py-2 px-4 rounded font-medium transition ${
                                portfolioType === 'allVas'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            All VAS
                            <span className="block text-xs">100% AU</span>
                        </button>
                        <button
                            onClick={() => applyPreset('allVgs')}
                            className={`py-2 px-4 rounded font-medium transition ${
                                portfolioType === 'allVgs'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            All VGS
                            <span className="block text-xs">100% Global</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            VAS Allocation: {vasPercentage}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={vasPercentage}
                            onChange={(e) => {
                                setVasPercentage(Number(e.target.value));
                                setPortfolioType('custom');
                            }}
                            className="w-full"
                        />
                        <p className="text-xs text-gray-600 mt-1">Australian Shares (ASX 300)</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            VGS Allocation: {vgsPercentage}%
                        </label>
                        <div className="h-2 bg-gray-200 rounded-full mt-8">
                            <div
                                className="h-2 bg-green-500 rounded-full transition-all"
                                style={{ width: `${vgsPercentage}%` }}
                            ></div>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">International Shares (ex-Australia)</p>
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex justify-center">
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, value }) => `${name}: ${value}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-blue-50 p-4 rounded">
                        <h3 className="font-semibold text-gray-800 mb-2">Portfolio Summary</h3>
                        <div className="space-y-1 text-sm">
                            <p><strong>VAS:</strong> ${Math.round(initialInvestment * (vasPercentage / 100)).toLocaleString()} ({vasPercentage}%)</p>
                            <p><strong>VGS:</strong> ${Math.round(initialInvestment * (vgsPercentage / 100)).toLocaleString()} ({vgsPercentage}%)</p>
                            <p className="pt-2 border-t"><strong>Blended Return:</strong> {calculations.blendedReturn.toFixed(2)}% p.a.</p>
                            <p><strong>Blended Dividend:</strong> {calculations.blendedDividend.toFixed(2)}% p.a.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Expected Returns (Advanced Settings)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            VAS Expected Return: {vasReturn}% p.a.
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="15"
                            step="0.1"
                            value={vasReturn}
                            onChange={(e) => setVasReturn(Number(e.target.value))}
                            className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Historical: ~9% (10yr avg)</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            VGS Expected Return: {vgsReturn}% p.a.
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="15"
                            step="0.1"
                            value={vgsReturn}
                            onChange={(e) => setVgsReturn(Number(e.target.value))}
                            className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Historical: ~10% (10yr avg)</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            VAS Dividend Yield: {vasDividend}% p.a.
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="8"
                            step="0.1"
                            value={vasDividend}
                            onChange={(e) => setVasDividend(Number(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            VGS Dividend Yield: {vgsDividend}% p.a.
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="5"
                            step="0.1"
                            value={vgsDividend}
                            onChange={(e) => setVgsDividend(Number(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Franked Dividends: {frankedPercentage}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={frankedPercentage}
                            onChange={(e) => setFrankedPercentage(Number(e.target.value))}
                            className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">VAS dividends that are franked</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Management Fees: {managementFee}% p.a.
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={managementFee}
                            onChange={(e) => setManagementFee(Number(e.target.value))}
                            className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">VAS: 0.07%, VGS: 0.18%</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Investment Summary (Year {investmentYears})</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded">
                        <p className="text-sm text-gray-600">Total Value</p>
                        <p className="text-2xl font-bold text-blue-700">${(calculations.finalValue / 1000).toFixed(0)}K</p>
                    </div>

                    <div className="text-center p-4 bg-gray-50 rounded">
                        <p className="text-sm text-gray-600">Total Contributed</p>
                        <p className="text-2xl font-bold text-gray-700">${(calculations.totalContributed / 1000).toFixed(0)}K</p>
                    </div>

                    <div className="text-center p-4 bg-green-50 rounded">
                        <p className="text-sm text-gray-600">Total Gain</p>
                        <p className="text-2xl font-bold text-green-700">${(calculations.totalGain / 1000).toFixed(0)}K</p>
                    </div>

                    <div className="text-center p-4 bg-purple-50 rounded">
                        <p className="text-sm text-gray-600">Total Return</p>
                        <p className="text-2xl font-bold text-purple-700">{calculations.totalReturnPercent}%</p>
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-yellow-50 rounded">
                        <p className="text-sm text-gray-600">Total Dividends Received</p>
                        <p className="text-2xl font-bold text-yellow-700">${(calculations.totalDividends / 1000).toFixed(0)}K</p>
                    </div>

                    <div className="text-center p-4 bg-green-50 rounded">
                        <p className="text-sm text-gray-600">Annual Dividend Income</p>
                        <p className="text-2xl font-bold text-green-700">${(calculations.annualDividendIncome / 1000).toFixed(1)}K</p>
                        <p className="text-xs text-gray-600 mt-1">${calculations.monthlyDividendIncome.toFixed(0)}/month</p>
                    </div>

                    <div className="text-center p-4 bg-red-50 rounded">
                        <p className="text-sm text-gray-600">Total Fees Paid</p>
                        <p className="text-2xl font-bold text-red-600">${(calculations.totalFees / 1000).toFixed(1)}K</p>
                    </div>
                </div>

                {debtRecyclingMode && (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-purple-50 rounded">
                            <p className="text-sm text-gray-600">Loan Balance</p>
                            <p className="text-2xl font-bold text-purple-700">${(calculations.finalLoanBalance / 1000).toFixed(0)}K</p>
                        </div>
                        <div className="text-center p-4 bg-indigo-50 rounded">
                            <p className="text-sm text-gray-600">Your Equity</p>
                            <p className="text-2xl font-bold text-indigo-700">${(calculations.finalEquity / 1000).toFixed(0)}K</p>
                        </div>
                        <div className="text-center p-4 bg-red-50 rounded">
                            <p className="text-sm text-gray-600">Total Interest Paid</p>
                            <p className="text-2xl font-bold text-red-700">${(calculations.totalInterestPaid / 1000).toFixed(0)}K</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded">
                            <p className="text-sm text-gray-600">Tax Saved</p>
                            <p className="text-2xl font-bold text-green-700">${(calculations.totalTaxSaved / 1000).toFixed(0)}K</p>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Portfolio Growth Over Time</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={calculations.projection}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                        <YAxis label={{ value: 'Value ($)', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                        <Legend />
                        <Line type="monotone" dataKey="totalValue" stroke="#8b5cf6" name="Total Value" strokeWidth={2} />
                        <Line type="monotone" dataKey="contributed" stroke="#6b7280" name="Contributed" strokeWidth={2} />
                        <Line type="monotone" dataKey="realValue" stroke="#f59e0b" name="Real Value (Inflation Adj.)" strokeWidth={2} strokeDasharray="5 5" />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Portfolio Composition Over Time</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={calculations.projection}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                        <YAxis label={{ value: 'Value ($)', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                        <Legend />
                        <Bar dataKey="vasValue" stackId="a" fill="#3b82f6" name="VAS Value" />
                        <Bar dataKey="vgsValue" stackId="a" fill="#10b981" name="VGS Value" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700">Year-by-Year Breakdown</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                        <tr>
                            <th className="p-2 text-left">Year</th>
                            <th className="p-2 text-right">Contributed</th>
                            <th className="p-2 text-right">Dividends</th>
                            <th className="p-2 text-right">Growth</th>
                            <th className="p-2 text-right">Fees</th>
                            {debtRecyclingMode && <th className="p-2 text-right">Interest</th>}
                            {debtRecyclingMode && <th className="p-2 text-right">Tax Saved</th>}
                            {debtRecyclingMode && <th className="p-2 text-right">Net Cash Flow</th>}
                            {debtRecyclingMode && <th className="p-2 text-right">Loan Bal</th>}
                            {debtRecyclingMode && <th className="p-2 text-right">Equity</th>}
                            <th className="p-2 text-right">Total Value</th>
                            <th className="p-2 text-right">Gain/Loss</th>
                            <th className="p-2 text-right">Return %</th>
                        </tr>
                        </thead>
                        <tbody>
                        {calculations.projection.map((row) => (
                            <tr key={row.year} className="border-b hover:bg-gray-50">
                                <td className="p-2 font-medium">{row.year}</td>
                                <td className="p-2 text-right text-gray-600">${row.contributed.toLocaleString()}</td>
                                <td className="p-2 text-right text-yellow-600">${row.dividends.toLocaleString()}</td>
                                <td className="p-2 text-right text-green-600">${row.growth.toLocaleString()}</td>
                                <td className="p-2 text-right text-red-600">-${row.fees.toLocaleString()}</td>
                                {debtRecyclingMode && <td className="p-2 text-right text-red-600">-${row.yearInterest.toLocaleString()}</td>}
                                {debtRecyclingMode && <td className="p-2 text-right text-green-600">+${row.yearTaxSaved.toLocaleString()}</td>}
                                {debtRecyclingMode && (
                                    <td className={row.netCashFlow >= 0 ? 'p-2 text-right font-semibold text-green-700' : 'p-2 text-right font-semibold text-red-700'}>
                                        ${row.netCashFlow.toLocaleString()}
                                    </td>
                                )}
                                {debtRecyclingMode && <td className="p-2 text-right text-gray-700">${row.loanBalance.toLocaleString()}</td>}
                                {debtRecyclingMode && <td className="p-2 text-right text-indigo-600 font-semibold">${row.equity.toLocaleString()}</td>}
                                <td className="p-2 text-right text-blue-700 font-semibold">${row.totalValue.toLocaleString()}</td>
                                <td className={`p-2 text-right font-semibold ${row.gainLoss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    ${row.gainLoss.toLocaleString()}
                                </td>
                                <td className={`p-2 text-right ${parseFloat(row.returnPercent) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    {row.returnPercent}%
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Investment Insights</h2>
                <div className="space-y-3 text-sm">
                    {debtRecyclingMode && (
                        <div>
                            <div className="bg-purple-100 p-3 rounded border border-purple-300 mb-3">
                                <p className="font-semibold text-purple-800">Debt Recycling Strategy</p>
                                <p className="text-gray-800 mt-1">Borrowing to invest with tax-deductible interest. Your effective rate after tax is {calculations.effectiveInterestRate.toFixed(2)}%.</p>
                            </div>
                            <div className="bg-white p-3 rounded mb-3">
                                <p className="font-semibold text-green-700">Tax Benefits</p>
                                <p className="text-gray-700 mt-1">Over {investmentYears} years you will save ${(calculations.totalTaxSaved / 1000).toFixed(0)}K in tax deductions.</p>
                            </div>
                            {(calculations.blendedReturn <= calculations.effectiveInterestRate) && (
                                <div className="bg-yellow-50 p-3 rounded border border-yellow-200 mb-3">
                                    <p className="text-yellow-800">⚠️ Warning: Your expected portfolio return ({calculations.blendedReturn.toFixed(2)}%) is less than or close to your after-tax borrowing cost ({calculations.effectiveInterestRate.toFixed(2)}%). Debt recycling may not be beneficial.
                                    </p>
                                </div>
                            )}
                            {loanType === 'IO' ? (
                                <div className="bg-white p-3 rounded">
                                    <p className="font-semibold text-purple-700">Interest Only Loans</p>
                                    <p className="text-gray-700 mt-1">Lower repayments and maximum tax deductions, but the loan balance does not reduce. Consider how you will eventually repay the principal.</p>
                                </div>
                            ) : (
                                <div className="bg-white p-3 rounded">
                                    <p className="font-semibold text-purple-700">Principal & Interest Loans</p>
                                    <p className="text-gray-700 mt-1">Higher repayments reduce the loan balance each year, lowering interest and tax deductions over time while building equity faster.</p>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-blue-700">📊 What are VAS and VGS?</p>
                        <p className="text-gray-700 mt-1"><strong>VAS</strong> tracks the ASX 300 (top 300 Australian companies). <strong>VGS</strong> tracks ~1,400 companies from major developed countries excluding Australia.</p>
                    </div>

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-green-700">💰 Dividend Income</p>
                        <p className="text-gray-700 mt-1">VAS typically pays higher dividends (~4%) with franking credits (tax benefits for Australian investors). VGS pays lower dividends (~2%) but historically has stronger capital growth.</p>
                    </div>

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-purple-700">🎯 Diversification</p>
                        <p className="text-gray-700 mt-1">A 50/50 split gives you exposure to both Australian and international markets. Growth-focused investors often prefer more VGS (70-100%), while income-focused prefer more VAS.</p>
                    </div>

                    {vasPercentage === 100 && (
                        <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                            <p className="text-yellow-800">⚠️ <strong>100% VAS:</strong> You're only invested in Australian companies. Consider adding VGS for international diversification to reduce country-specific risk.</p>
                        </div>
                    )}

                    {vgsPercentage === 100 && (
                        <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                            <p className="text-yellow-800">⚠️ <strong>100% VGS:</strong> No exposure to Australian market or franking credits. Consider adding some VAS for local exposure and tax-effective dividends.</p>
                        </div>
                    )}

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-indigo-700">🔄 Dividend Reinvestment</p>
                        <p className="text-gray-700 mt-1">{reinvestDividends ? "You're reinvesting dividends, which compounds your returns over time. This is generally recommended for long-term wealth building." : "You're taking dividends as cash income. This reduces compounding but provides regular income."}</p>
                    </div>

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-orange-700">🎓 Franking Credits</p>
                        <p className="text-gray-700 mt-1">VAS dividends are typically {frankedPercentage}% franked, meaning the company has already paid 30% tax. You can claim this as a credit, making dividends more tax-effective than salary income at most tax brackets.</p>
                    </div>

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-red-700">💸 Management Fees</p>
                        <p className="text-gray-700 mt-1">VAS charges 0.07% p.a. and VGS charges 0.18% p.a. - among the lowest in the market. Over {investmentYears} years, you'll pay approximately ${(calculations.totalFees / 1000).toFixed(1)}K in fees.</p>
                    </div>

                    {calculations.totalReturnPercent > 100 && (
                        <div className="bg-green-50 p-3 rounded border border-green-200">
                            <p className="text-green-800">✅ <strong>Strong projected returns:</strong> Your portfolio could return {calculations.totalReturnPercent}% over {investmentYears} years, turning ${(calculations.totalContributed / 1000).toFixed(0)}K into ${(calculations.finalValue / 1000).toFixed(0)}K.</p>
                        </div>
                    )}

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-gray-700">⏰ Dollar Cost Averaging</p>
                        <p className="text-gray-700 mt-1">By contributing ${monthlyContribution}/month, you're using dollar cost averaging - buying more units when prices are low and fewer when high. This reduces timing risk.</p>
                    </div>

                    <div className="bg-white p-3 rounded">
                        <p className="font-semibold text-gray-700">📈 Past Performance Note</p>
                        <p className="text-gray-700 mt-1">These projections use historical averages, but past performance doesn't guarantee future returns. Markets can be volatile - always invest based on your risk tolerance and time horizon.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ETFInvestmentAnalysis;
